import { useState, useEffect, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { getPartnersByTournament } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import MobilePartnerSheet from '../../components/MobilePartnerSheet';
import MobilePartnerDetailsSheet from '../../components/MobilePartnerDetailsSheet';
import { toast } from 'react-hot-toast';
import type { Partner } from '../../../types/content';

interface FormattedPartner {
  id: string;
  company: string;
  logo: string;
  contactPerson: string;
  email: string;
  phone: string;
  website?: string;
  tier: string;
  status: string;
  contribution: string;
  contributionAmount?: number;
  startDate: string;
  endDate: string;
  benefits: string[];
  description?: string;
  stats?: string[];
  image?: string;
}

function tierColor(tier: string) {
  if (tier.includes('Title')) return 'bg-amber-100 text-amber-800';
  if (tier.includes('Diamond')) return 'bg-blue-100 text-blue-800';
  if (tier.includes('Platinum')) return 'bg-slate-100 text-slate-700';
  if (tier.includes('Gold')) return 'bg-yellow-100 text-yellow-700';
  if (tier.includes('Silver')) return 'bg-gray-100 text-gray-600';
  return 'bg-green-100 text-green-700';
}

function statusColor(status: string) {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-700';
    case 'pending': return 'bg-amber-100 text-amber-700';
    case 'expired': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
}

export default function MobileAdminPartners() {
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';
  const [partners, setPartners] = useState<FormattedPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<FormattedPartner | null>(null);

  useEffect(() => { if (tournamentId) fetchPartners(); }, [tournamentId]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const { data, error } = await getPartnersByTournament(tournamentId);
      if (error) throw error;
      const formatted: FormattedPartner[] = (data || []).map((p: Partner) => ({
        id: p.id,
        company: p.company_name || '',
        logo: p.logo_url || '',
        contactPerson: p.contact_person || '',
        email: p.email || '',
        phone: p.phone || '',
        website: p.website,
        tier: p.tier || '',
        status: p.status || '',
        contribution: p.contribution ? `₦${(p.contribution / 1_000_000).toFixed(1)}M` : '₦0',
        contributionAmount: p.contribution,
        startDate: p.start_date ? new Date(p.start_date).toLocaleDateString() : '',
        endDate: p.end_date ? new Date(p.end_date).toLocaleDateString() : '',
        benefits: p.benefits || [],
        description: p.description,
      }));
      setPartners(formatted);
    } catch {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => partners.filter(p => {
    const matchSearch = p.company.toLowerCase().includes(search.toLowerCase()) ||
      p.contactPerson.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || p.status.toLowerCase() === statusFilter.toLowerCase();
    return matchSearch && matchStatus;
  }), [partners, search, statusFilter]);

  const totalValue = partners.reduce((sum, p) => sum + (p.contributionAmount || 0), 0);
  const active = partners.filter(p => p.status === 'active').length;
  const pending = partners.filter(p => p.status === 'pending').length;

  return (
    <div className="pb-4 space-y-4">
      {/* Stats */}
      <div className="px-4 pt-4 grid grid-cols-2 gap-3">
        {[
          { label: 'Total', value: partners.length, icon: BuildingOfficeIcon, color: 'text-teal-600 bg-teal-50' },
          { label: 'Total Value', value: totalValue >= 1_000_000 ? `₦${(totalValue / 1_000_000).toFixed(1)}M` : `₦${totalValue}`, icon: BanknotesIcon, color: 'text-green-600 bg-green-50' },
          { label: 'Active', value: active, icon: CheckCircleIcon, color: 'text-blue-600 bg-blue-50' },
          { label: 'Pending', value: pending, icon: ClockIcon, color: 'text-amber-600 bg-amber-50' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-lg font-bold text-golf-gray">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Sticky Search + Filters */}
      <div className="sticky top-14 z-10 bg-gray-50 px-4 pt-2 pb-3 space-y-2 border-b border-gray-100">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search partners..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
            />
          </div>
          <button
            onClick={() => setIsFormOpen(true)}
            className="bg-golf-orange text-white rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-sm font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
          {['All', 'Active', 'Pending', 'Expired'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                statusFilter === s ? 'bg-golf-orange text-white border-golf-orange' : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="px-4 flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-4 text-center py-12 text-gray-400 text-sm">No partners found</div>
      ) : (
        <div className="px-4 space-y-2">
          {filtered.map(partner => (
            <button
              key={partner.id}
              onClick={() => setSelectedPartner(partner)}
              className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-left active:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                {partner.logo ? (
                  <img src={partner.logo} alt={partner.company} className="w-10 h-10 rounded-xl object-contain border border-gray-100 bg-gray-50" />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                    <BuildingOfficeIcon className="w-5 h-5 text-teal-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-golf-gray text-sm truncate">{partner.company}</p>
                  <p className="text-xs text-gray-500 truncate">{partner.contactPerson}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${tierColor(partner.tier)}`}>{partner.tier}</span>
                    <span className="text-xs text-gray-500 font-medium">{partner.contribution}</span>
                  </div>
                </div>
                <span className={`flex-shrink-0 text-xs px-2 py-1 rounded-full font-medium capitalize ${statusColor(partner.status)}`}>
                  {partner.status}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      <MobilePartnerSheet
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); fetchPartners(); }}
      />

      {selectedPartner && (
        <MobilePartnerDetailsSheet
          isOpen={!!selectedPartner}
          onClose={() => { setSelectedPartner(null); fetchPartners(); }}
          partner={selectedPartner}
          tournamentId={tournamentId}
        />
      )}
    </div>
  );
}
